import { useEffect, useState } from 'react'
import useAsync from 'react-use/esm/useAsync'
import { CurieSelect } from '~/components/common/CurieSelect'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import {
  AgentRequest,
  SourceOutputExampleType,
  sourceOutputExampleTypeFromJSON,
  sourceOutputExampleTypeToJSON
} from '~/protos/agents'
import { toCamelCase } from '~/utils/shared'
import { TaskParams } from './BaseTaskForm'
import { InfoTooltip } from './shared/InfoToolTip'

interface GenerateDataweaveForm {
  onChange: (task: TaskParams) => void
  dataweaveDefaultValue?: string
  exampleTypeDefaultValue?: SourceOutputExampleType
  initNotes?: string
  withExamplePrefilled?: boolean
  // VM args related
  vmAware?: boolean
  initVmArgs?: string
  suggestedVmArgs?: string
  isLoadingSuggestedVmArgs?: boolean
  hideVmArgs?: boolean
}

export function SampleDataGeneratorForm({
  onChange,
  dataweaveDefaultValue = '',
  exampleTypeDefaultValue = SourceOutputExampleType.DATA_TYPE_JSON,
  initNotes = '',
  withExamplePrefilled = false,
  vmAware = false,
  initVmArgs = '',
  suggestedVmArgs = '',
  isLoadingSuggestedVmArgs = false,
  hideVmArgs = false
}: GenerateDataweaveForm) {
  const initialDataweaveCode = withExamplePrefilled
    ? `%dw 2.0
output application/json
---
{
  orderId: payload.orderId,
  totalCost: payload.items map ((item) -> {
    itemId: item.itemId,
    totalItemCost: vars.pricing[item.itemId] * item.quantity
  }),
  transactionDetails: {
    date: attributes.transactionDate,
    customer: attributes.customerName
  }
}
`.trim()
    : dataweaveDefaultValue
  const [exampleType, setExampleType] = useState<SourceOutputExampleType>(exampleTypeDefaultValue)
  const [dataweaveCode, setDataweaveCode] = useState<string>(initialDataweaveCode)
  const [additionalNotes, setAdditionalNotes] = useState<string>(initNotes)
  // Only use explicit initial VM args, not suggested ones (those are placeholders)
  const [vmArgs, setVMArgs] = useState<string>(initVmArgs)

  // Update dataweaveCode when withExamplePrefilled changes
  useEffect(() => {
    const newCode = withExamplePrefilled
      ? `%dw 2.0
output application/json
---
{
  orderId: payload.orderId,
  totalCost: payload.items map ((item) -> {
    itemId: item.itemId,
    totalItemCost: vars.pricing[item.itemId] * item.quantity
  }),
  transactionDetails: {
    date: attributes.transactionDate,
    customer: attributes.customerName
  }
}
`.trim()
      : '' // Clear the value when unchecking Try Example
    setDataweaveCode(newCode)
  }, [withExamplePrefilled])

  useEffect(() => {
    setVMArgs(initVmArgs)
  }, [initVmArgs])

  useAsync(async () => {
    const request = AgentRequest.fromPartial({
      command: { commandString: dataweaveCode },
      dwlToInputOutputExample: {
        commandString: dataweaveCode,
        exampleType
      },
      userNotes: additionalNotes,
      vmArgs
    })
    onChange?.({ request })
  }, [exampleType, dataweaveCode, additionalNotes, vmArgs])

  const items = Object.keys(SourceOutputExampleType)
    .filter(
      type =>
        type === 'DATA_TYPE_JSON' ||
        type === 'DATA_TYPE_CSV' ||
        type === 'DATA_TYPE_YAML' ||
        type === 'DATA_TYPE_XML'
    )
    .map(key => {
      const label = toCamelCase(key).split(' ').pop()!.toUpperCase()
      return { label, key: key }
    })

  return (
    <div className='input-output-form'>
      <div className='input-type'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
          Input Data Type:
        </div>
        <CurieSelect
          items={items}
          placeholder='Select input format'
          selectedKeys={[sourceOutputExampleTypeToJSON(exampleType)]}
          disabled={withExamplePrefilled}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const format = selectedKeys.values().next().value as string
            setExampleType(sourceOutputExampleTypeFromJSON(format))
          }}
        />
      </div>
      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
          DataWeave:
        </div>
        <SimpleTextarea
          onChange={setDataweaveCode}
          minRows={20}
          maxRows={20}
          inputClassName='h-[320px]'
          value={withExamplePrefilled ? dataweaveCode : undefined}
          defaultValue={withExamplePrefilled ? undefined : initialDataweaveCode}
          disabled={withExamplePrefilled}
          placeholder={`
%dw 2.0
output application/json
---
{
  orderId: payload.orderId,
  totalCost: payload.items map ((item) -> {
    itemId: item.itemId,
    totalItemCost: vars.pricing[item.itemId] * item.quantity
  }),
  transactionDetails: {
    date: attributes.transactionDate,
    customer: attributes.customerName
  }
}
`.trim()}
        />
      </div>
      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
          <span>Notes (Optional):</span>
          <InfoTooltip content='Provides sample data for each test scenario to ensure realistic and effective testing.' />
        </div>
        <SimpleTextarea
          onChange={setAdditionalNotes}
          minRows={2}
          defaultValue={initNotes}
          disabled={withExamplePrefilled}
          placeholder='Additional notes about what kind of data you want to generate etc. It helps us better understand your needs.'
        />
      </div>
      {vmAware && !hideVmArgs ? (
        <>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
            <div className='flex items-center gap-2'>
              <span>VM Args:</span>
              {isLoadingSuggestedVmArgs && (
                <div className='flex items-center gap-1 text-xs text-gray-500 select-none'>
                  <svg className='animate-spin h-3 w-3' viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                      fill='none'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Loading VM args suggestions from repository...</span>
                </div>
              )}
            </div>
            <InfoTooltip content='If running the test requires VM arguments, users can supply them to ensure the tests execute correctly within the appropriate environment settings.' />
          </div>
          <SimpleTextarea
            className='mx-0'
            value={vmArgs}
            onChange={setVMArgs}
            minRows={2}
            disabled={withExamplePrefilled}
            placeholder={suggestedVmArgs || ''}
          />
        </>
      ) : null}
    </div>
  )
}
